import { clientMoveIn } from "../lib/clientMoveIn";
// src/components/GuestCardDialog.tsx
import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Chip,
  alpha,
  useTheme,
  useMediaQuery,
  Avatar,
  Autocomplete,
  InputAdornment,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from "@mui/material";
import {
  Close as CloseIcon,
  Send as SendIcon,
  Download as DownloadIcon,
  Visibility as PreviewIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  Check as CheckIcon,
  Message as MessageIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ContactMail as ContactMailIcon,
} from "@mui/icons-material";
import {
  emailService,
  generateGuestCardHtml,
  checkGmailStatus,
} from "../services/emailService";
import type {
  ClientInfo,
  GuestCardData,
  GmailStatus,
} from "../services/emailService";
import { supabase } from "../lib/supabase";
import { fetchWithAuth } from "../lib/api";

/**
 * What actually happened, not merely THAT something happened.
 *
 * This dialog has two terminal paths that mean different things: emailing the
 * card to selected contacts, and PKT-0189's recipient-less download that
 * registers the property with no email at all. They shared one
 * `success: boolean`, so the single send-worded banner interpolated
 * `selectedContacts.length` and a download rendered "Guest card sent
 * successfully to 0 contacts!" in production. A discriminated outcome makes
 * the banner unable to describe a path that did not run.
 */
type SendOutcome =
  | null
  | { kind: "emailed"; count: number }
  | { kind: "downloaded"; property: string };

/**
 * Thrown when the caller has ALREADY put a specific message on screen.
 *
 * handleDownload's catch used to guard with `if (!error)` — a read of the
 * `error` STATE, which in that same tick is still the pre-render value. The
 * guard was therefore always true and the generic "Failed to download guest
 * card" overwrote the specific registration-failure copy every single time.
 * A marker on the error carries the fact reliably; component state cannot.
 */
class ReportedRegistrationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ReportedRegistrationError";
  }
}

interface GuestCardDialogProps {
  open: boolean;
  onClose: () => void;
  client: ClientInfo | null;
  onSuccess?: () => void;
  onNavigateToSettings?: () => void;
  isDemo?: boolean;
}

interface PropertyContact {
  id: string;
  name: string;
  email: string;
  title?: string;
}

interface Property {
  id: string;
  name: string;
  neighborhood?: string;
  contacts?: PropertyContact[];
}

const GuestCardDialog: React.FC<GuestCardDialogProps> = ({
  open,
  onClose,
  isDemo = false,
  client,
  onSuccess,
  onNavigateToSettings,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<SendOutcome>(null);

  // Gmail status
  const [gmailStatus, setGmailStatus] = useState<GmailStatus>({
    connected: false,
    email: null,
    connected_at: null,
    last_used_at: null,
  });
  const [checkingGmail, setCheckingGmail] = useState(true);

  // Form state
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(
    null
  );
  const [selectedContacts, setSelectedContacts] = useState<PropertyContact[]>(
    []
  );
  const [bedsBaths, setBedsBaths] = useState("");
  const [additionalComments, setAdditionalComments] = useState("");

  // Add contact form state
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContactName, setNewContactName] = useState("");
  const [newContactEmail, setNewContactEmail] = useState("");
  const [newContactTitle, setNewContactTitle] = useState("");
  const [savingContact, setSavingContact] = useState(false);

  // Locator info (loaded from email config and profile)
  const [locatorName, setLocatorName] = useState("");
  const [locatorPhone, setLocatorPhone] = useState("");
  const [locatorEmail, setLocatorEmail] = useState("");
  const [locatorJobTitle, setLocatorJobTitle] = useState("");
  const [locatorPhotoUrl, setLocatorPhotoUrl] = useState("");

  // Properties list
  const [properties, setProperties] = useState<Property[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);

  // Preview HTML
  const [previewHtml, setPreviewHtml] = useState("");
  const previewScaleContainerRef = useRef<HTMLDivElement>(null);
  const [previewScale, setPreviewScale] = useState<number>(1);

  // Scale the 600px guest-card preview to fit the mobile viewport.
  useEffect(() => {
    if (!isMobile) {
      setPreviewScale(1);
      return;
    }
    const node = previewScaleContainerRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const compute = () => {
      const w = node.clientWidth;
      if (w > 0) setPreviewScale(Math.min(1, w / 600));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(node);
    return () => ro.disconnect();
  }, [isMobile, previewHtml, showPreview]);

  // Check Gmail connection on open
  useEffect(() => {
    if (open) {
      setCheckingGmail(true);
      checkGmailStatus().then((status) => {
        setGmailStatus(status);
        setCheckingGmail(false);
      });
    }
  }, [open]);

  // Initialize form on open
  useEffect(() => {
    if (open) {
      loadProperties();
      resetForm();

      // Load locator config from emailService
      const config = emailService.getConfig();
      setLocatorName(config.agentName || "");
      setLocatorPhone(config.agentPhone || "");
      setLocatorEmail(config.agentEmail || "");
      setLocatorJobTitle(config.jobTitle || "");
    }
  }, [open]);

  // Demo: auto-select first property when properties load
  useEffect(() => {
    if (isDemo && open && properties.length > 0 && !selectedProperty) {
      handlePropertyChange(properties[0]);
    }
  }, [isDemo, open, properties]);

  // Fetch locator's avatar_url from profiles table
  useEffect(() => {
    const fetchLocatorPhoto = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from("profiles")
            .select("avatar_url, full_name, phone, job_title")
            .eq("id", user.id)
            .single();

          if (profile) {
            if (profile.avatar_url) {
              setLocatorPhotoUrl(profile.avatar_url);
            }
            // Fill in any missing locator info from profile
            if (!locatorName && profile.full_name) {
              setLocatorName(profile.full_name);
            }
            if (!locatorPhone && profile.phone) {
              setLocatorPhone(profile.phone);
            }
            if (!locatorJobTitle && profile.job_title) {
              setLocatorJobTitle(profile.job_title);
            }
          }
        }
      } catch (err) {
        console.error("Error fetching locator photo:", err);
      }
    };

    if (open) {
      fetchLocatorPhoto();
    }
  }, [open, locatorName, locatorPhone, locatorJobTitle]);

  // Pre-fill from client data
  useEffect(() => {
    if (client) {
      // Pre-fill beds/baths from client preferences
      if (client.min_bedrooms) {
        setBedsBaths(`${client.min_bedrooms}+ Bed`);
      }
      // Do NOT pre-fill additional comments from client.notes: those are the
      // operator's PRIVATE CRM notes and this field is sent verbatim to the
      // leasing office. Start blank so only intentionally-typed text ships.
    }
  }, [client]);

  // Update preview when showing preview
  useEffect(() => {
    if (showPreview && client) {
      updatePreview();
    }
  }, [
    showPreview,
    client,
    selectedContacts,
    selectedProperty,
    bedsBaths,
    additionalComments,
    locatorName,
    locatorPhone,
    locatorEmail,
    locatorJobTitle,
    locatorPhotoUrl,
  ]);

  const loadProperties = async () => {
    setLoadingProperties(true);
    try {
      // Try to fetch properties with contacts column
      let query = supabase
        .from("properties")
        .select("id, name, neighborhood, contacts");
      if (isDemo) {
        query = query.eq("demo_visible", true);
      }
      const { data, error } = await query.order("name");

      if (error) {
        // If contacts column doesn't exist, try without it
        if (error.message?.includes("contacts")) {
          console.warn("contacts column not found, fetching without it");
          let fallbackQuery = supabase
            .from("properties")
            .select("id, name, neighborhood");
          if (isDemo) {
            fallbackQuery = fallbackQuery.eq("demo_visible", true);
          }
          const { data: fallbackData, error: fallbackError } = await fallbackQuery.order("name");

          if (fallbackError) {
            console.error("Supabase error:", fallbackError);
            throw fallbackError;
          }

          // Map fallback data to include empty contacts array (or demo placeholder)
          const demoEmail = "info@locatenowatl.com";
          const propertiesWithoutContacts = (fallbackData || []).map(
            (property: any) => ({
              ...property,
              contacts: isDemo
                ? [{ id: "demo-contact", name: "Leasing Office", email: demoEmail, title: "Leasing Consultant" }]
                : [],
            })
          );
          setProperties(propertiesWithoutContacts);
          return;
        }

        console.error("Supabase error:", error);
        throw error;
      }

      // Parse contacts and ensure they have IDs
      const propertiesWithContacts = (data || []).map((property: any) => ({
        ...property,
        contacts: ((property.contacts as any[]) || []).map(
          (contact: any, index: number) => ({
            id: contact.id || `contact-${index}-${Date.now()}`,
            name: contact.name || "",
            email: contact.email || "",
            title: contact.title || "",
          })
        ),
      }));

      // Demo users: replace real contacts with placeholder
      if (isDemo) {
        const demoEmail = "info@locatenowatl.com";
        const demoProperties = propertiesWithContacts.map((p: any) => ({
          ...p,
          contacts: [
            {
              id: "demo-contact",
              name: "Leasing Office",
              email: demoEmail,
              title: "Leasing Consultant",
            },
          ],
        }));
        setProperties(demoProperties);
      } else {
        setProperties(propertiesWithContacts);
      }
    } catch (err: any) {
      console.error("Error loading properties:", err);
      setError(`Failed to load properties: ${err.message || "Unknown error"}`);
    } finally {
      setLoadingProperties(false);
    }
  };

  const resetForm = () => {
    setSelectedProperty(null);
    setSelectedContacts([]);
    setBedsBaths("");
    setAdditionalComments("");
    setError(null);
    setOutcome(null);
    setShowPreview(false);
    setShowAddContact(false);
    resetNewContactForm();
  };

  const resetNewContactForm = () => {
    setNewContactName("");
    setNewContactEmail("");
    setNewContactTitle("");
  };

  const updatePreview = () => {
    // Gated on PROPERTY, not on recipients. The download path builds the very
    // same card with `recipientEmail: ""`, and both paths render through this
    // one client-side generateGuestCardHtml — so there is nothing to preview
    // that a selected contact makes available and a property does not.
    if (!client || !selectedProperty) return;

    // With no contacts selected this is exactly the card handleDownload builds.
    const data: GuestCardData = {
      client,
      recipientEmail: selectedContacts.map((c) => c.email).join(", "),
      propertyName: selectedProperty?.name,
      bedsBaths,
      additionalComments,
      locatorName,
      locatorPhone,
      locatorEmail,
      locatorJobTitle,
      locatorPhotoUrl,
    };

    setPreviewHtml(generateGuestCardHtml(data));
  };

  // When property is selected, clear selected contacts (auto-select for demo)
  const handlePropertyChange = (property: Property | null) => {
    setSelectedProperty(property);
    if (isDemo && property?.contacts?.length) {
      setSelectedContacts([...property.contacts]);
    } else {
      setSelectedContacts([]);
    }
    setShowAddContact(false);
    resetNewContactForm();
  };

  // Toggle contact selection
  const handleToggleContact = (contact: PropertyContact) => {
    setSelectedContacts((prev) => {
      const isSelected = prev.some((c) => c.id === contact.id);
      if (isSelected) {
        return prev.filter((c) => c.id !== contact.id);
      } else {
        return [...prev, contact];
      }
    });
  };

  // Select all contacts
  const handleSelectAllContacts = () => {
    if (!selectedProperty?.contacts) return;
    if (selectedContacts.length === selectedProperty.contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts([...selectedProperty.contacts]);
    }
  };

  // Add new contact to property
  const handleAddContact = async () => {
    if (!selectedProperty) return;
    if (!newContactName.trim() || !newContactEmail.trim()) {
      setError("Please enter contact name and email");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newContactEmail.trim())) {
      setError("Please enter a valid email address");
      return;
    }

    setSavingContact(true);
    setError(null);

    try {
      const newContact: PropertyContact = {
        id: `contact-${Date.now()}`,
        name: newContactName.trim(),
        email: newContactEmail.trim(),
        title: newContactTitle.trim() || undefined,
      };

      const updatedContacts = [
        ...(selectedProperty.contacts || []),
        newContact,
      ];

      // Save to database via backend API
      await fetchWithAuth(`/properties-management/${selectedProperty.id}`, {
        method: "PATCH",
        body: JSON.stringify({ contacts: updatedContacts }),
      });

      // Update local state
      const updatedProperty = {
        ...selectedProperty,
        contacts: updatedContacts,
      };

      setSelectedProperty(updatedProperty);
      setProperties((prev) =>
        prev.map((p) => (p.id === selectedProperty.id ? updatedProperty : p))
      );

      // Auto-select the new contact
      setSelectedContacts((prev) => [...prev, newContact]);

      // Reset form
      resetNewContactForm();
      setShowAddContact(false);
    } catch (err: any) {
      console.error("Error adding contact:", err);
      setError(err.message || "Failed to add contact");
    } finally {
      setSavingContact(false);
    }
  };

  // Save sent guest card to database
  const saveSentGuestCard = async (
    contact: PropertyContact,
    emailHtml: string,
    trackingId?: string,
    deliveryMethod: "emailed" | "downloaded" = "emailed",
  ) => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user || !client) return;

      const clientSnapshot = {
        name: `${client.first_name} ${client.last_name}`,
        email: client.email,
        phone: client.phone || null,
        budget: client.max_budget || null,
        move_in: clientMoveIn(client) || 'Flexible',
        bedrooms: client.min_bedrooms ? `${client.min_bedrooms}+ Bed` : null,
        preferred_areas: client.preferred_areas || [],
      };

      const propertySnapshot = selectedProperty
        ? {
            name: selectedProperty.name,
            neighborhood: selectedProperty.neighborhood || null,
          }
        : null;

      const locatorSnapshot = {
        name: locatorName,
        email: locatorEmail,
        phone: locatorPhone,
        title: locatorJobTitle || null,
        photo_url: locatorPhotoUrl || null,
      };

      const { error: insertError } = await supabase
        .from("sent_guest_cards")
        .insert({
          user_id: user.id,
          client_id: client.id,
          property_id: selectedProperty?.id || null,
          recipient_name: contact.name,
          recipient_email: contact.email,
          recipient_title: contact.title || null,
          client_snapshot: clientSnapshot,
          property_snapshot: propertySnapshot,
          locator_snapshot: locatorSnapshot,
          beds_baths: bedsBaths || null,
          additional_comments: additionalComments || null,
          email_subject: `Guest Card: ${client.first_name} ${client.last_name}`,
          email_html: emailHtml,
          status: "sent",
          delivery_method: deliveryMethod,
          ...(trackingId && { tracking_id: trackingId }),
        });
      if (insertError) throw insertError;
    } catch (err) {
      console.error("Error saving sent guest card:", err);
      // A failed SEND log is tolerable — the email already went out and the
      // operator saw it succeed. A failed DOWNLOAD log is not: the row IS the
      // registration, so a silent failure means the credit clock never started
      // and we lose a commission we earned. Surface it and re-raise.
      if (deliveryMethod === "downloaded") {
        // The caller closes the print window on this throw, so the card does
        // NOT reach the operator — say so, rather than the old wording that
        // claimed a successful download beside a failed registration.
        setError(
          "The registration was NOT recorded, so nothing was downloaded — " +
            "the credit clock has not started. Try the download again.",
        );
        throw new ReportedRegistrationError(
          err instanceof Error ? err.message : "registration insert failed",
        );
      }
    }
  };

  // Log activity with full details
  const logGuestCardActivity = async (contact: PropertyContact) => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user || !client) return;

      await supabase.from("activities").insert({
        user_id: user.id,
        activity_type: "guest_card_sent",
        entity_type: "client",
        entity_id: client.id,
        metadata: {
          client_name: `${client.first_name} ${client.last_name}`,
          client_email: client.email,
          property_id: selectedProperty?.id || null,
          property_name: selectedProperty?.name || null,
          recipient_name: contact.name,
          recipient_email: contact.email,
          recipient_title: contact.title || null,
          beds_baths: bedsBaths || null,
          move_in: clientMoveIn(client) || 'Flexible',
          budget: client.max_budget || null,
        },
      });
    } catch (err) {
      console.error("Error logging activity:", err);
    }
  };

  /**
   * PKT-0189 Slice A · Download the card and REGISTER it.
   *
   * Most properties have no leasing email on file, so the send path never runs
   * and the registration never happens — locator credit we earned is lost
   * silently. This renders the already-built card HTML into a print window
   * (browser print-to-PDF, no new dependency) and writes the registration row
   * so the credit clock starts even with no recipient.
   *
   * ORDERING: the print window is opened SYNCHRONOUSLY inside the click
   * handler, before any await. Opening it after `await saveSentGuestCard`
   * spent the user-gesture token and popup blockers routinely killed it; the
   * handler then returned without recording an outcome, `finally` re-enabled
   * the button, and the error told the operator to "download again" — a
   * compliant second click wrote a SECOND registration row.
   *
   * The original intent still holds and is preserved: a failed registration
   * must not leave the operator holding a PDF and believing we are on record.
   * The window opens EMPTY and is only written into once the row lands; if the
   * insert throws, the window is closed and the error stands.
   */
  const handleDownload = async () => {
    if (!client) return;
    setLoading(true);
    setError(null);
    try {
      const data: GuestCardData = {
        client: {
          ...client,
          move_in_timeline: clientMoveIn(client) || 'Flexible',
        },
        recipientEmail: "",
        propertyName: selectedProperty?.name,
        bedsBaths,
        additionalComments,
        locatorName,
        locatorPhone,
        locatorEmail,
        locatorJobTitle,
        locatorPhotoUrl,
      };
      const cardHtml = generateGuestCardHtml(data);

      // Opened here, still inside the user gesture — see ORDERING above.
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        // Nothing has been written yet, so this is a clean no-op: retrying
        // after allowing popups records exactly one registration.
        setError(
          "The download was blocked by a popup blocker. Nothing was " +
            "recorded — allow popups for this site, then download again.",
        );
        return;
      }

      try {
        // No recipient is the POINT of this path — never guess one. The row
        // records the property registration itself.
        await saveSentGuestCard(
          { id: "", name: "Hand-submitted", email: "" },
          cardHtml,
          undefined,
          "downloaded",
        );
      } catch (err) {
        // The row IS the registration. Without it, hand over nothing.
        printWindow.close();
        throw err;
      }

      printWindow.document.write(cardHtml);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      setOutcome({
        kind: "downloaded",
        property: selectedProperty?.name || "this property",
      });
    } catch (err) {
      // Only speak if nobody already has — see ReportedRegistrationError.
      if (!(err instanceof ReportedRegistrationError)) {
        setError(
          err instanceof Error ? err.message : "Failed to download guest card",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!client) return;
    if (selectedContacts.length === 0) {
      setError("Please select at least one contact to send to");
      return;
    }
    if (!isDemo && !gmailStatus.connected) {
      setError("Please connect your Gmail account in Settings to send emails");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Send to each selected contact
      const results = await Promise.all(
        selectedContacts.map(async (contact) => {
          const data: GuestCardData = {
            client: {
              ...client,
              // This is an outgoing preview payload; the stored timeline is unchanged.
              move_in_timeline: clientMoveIn(client) || 'Flexible',
            },
            recipientEmail: contact.email,
            propertyName: selectedProperty?.name,
            bedsBaths,
            additionalComments,
            locatorName,
            locatorPhone,
            locatorEmail,
            locatorJobTitle,
            locatorPhotoUrl,
          };

          const result = await emailService.sendGuestCard(data);

          // If successful, save to sent_guest_cards and log activity
          if (result.success) {
            const emailHtml = generateGuestCardHtml(data);
            await saveSentGuestCard(contact, emailHtml, result.trackingId);
            await logGuestCardActivity(contact);
          }

          return result;
        })
      );

      const allSuccess = results.every((r) => r.success);
      const failedCount = results.filter((r) => !r.success).length;

      if (allSuccess) {
        setOutcome({ kind: "emailed", count: selectedContacts.length });
        onSuccess?.();
        setTimeout(() => {
          onClose();
        }, 2000);
      } else if (failedCount < selectedContacts.length) {
        // Partial success
        setError(
          `Sent to ${selectedContacts.length - failedCount} of ${
            selectedContacts.length
          } contacts. Some failed.`
        );
        // Report what actually went out, not what was selected.
        setOutcome({
          kind: "emailed",
          count: selectedContacts.length - failedCount,
        });
      } else {
        setError(results[0]?.error || "Failed to send guest card");
      }
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleGoToSettings = () => {
    onClose();
    onNavigateToSettings?.();
  };

  if (!client) return null;

  const hasContacts =
    selectedProperty?.contacts && selectedProperty.contacts.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          minHeight: isMobile ? "100vh" : "70vh",
          maxHeight: isMobile ? "100vh" : "90vh",
          borderRadius: isMobile ? 0 : undefined,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, px: { xs: 2, sm: 3 } }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={1.25}
            sx={{ minWidth: 0, flex: 1 }}
          >
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: isMobile ? 32 : 40,
                height: isMobile ? 32 : 40,
                flexShrink: 0,
              }}
            >
              <EmailIcon fontSize={isMobile ? "small" : "medium"} />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant={isMobile ? "subtitle1" : "h6"}
                fontWeight={700}
                noWrap
              >
                Send Guest Card
              </Typography>
              {!isMobile && (
                <Typography variant="caption" color="text.secondary">
                  Send client information to property contacts
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ flexShrink: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5, pb: 2 }}>
        {/* Gmail Connection Warning (hidden for demo) */}
        {!isDemo && !checkingGmail && !gmailStatus.connected && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={handleGoToSettings}>
                Connect Gmail
              </Button>
            }
          >
            Gmail not connected. Connect your Gmail account in Settings to send
            emails.
          </Alert>
        )}

        {/* Gmail Connected Info */}
        {!isDemo && !checkingGmail && gmailStatus.connected && gmailStatus.email && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<CheckIcon />}>
            Sending from: <strong>{gmailStatus.email}</strong>
          </Alert>
        )}

        {/* Demo mode info */}
        {isDemo && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Demo mode — use Preview to see how guest cards look. Sending is disabled.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {outcome && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {outcome.kind === "downloaded" ? (
              <>
                Guest card downloaded and registered for{" "}
                <strong>{outcome.property}</strong>. No email was sent.
              </>
            ) : (
              <>
                Guest card sent successfully to {outcome.count} contact
                {outcome.count !== 1 ? "s" : ""}!
              </>
            )}
          </Alert>
        )}

        {!showPreview ? (
          <Grid container spacing={2.5}>
            {/* Client Info Summary */}
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <PersonIcon fontSize="small" color="primary" />
                  Client Information
                </Typography>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} md={6}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: theme.palette.primary.main,
                          fontSize: 14,
                        }}
                      >
                        {client.first_name[0]}
                        {client.last_name[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {client.first_name} {client.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {isDemo
                            ? "info@locatenowatl.com"
                            : client.email}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {client.phone && (
                        <Chip
                          label={client.phone}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {client.max_budget && (
                        <Chip
                          label={`$${client.max_budget.toLocaleString()}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      )}
                      {clientMoveIn(client) && (
                        <Chip
                          label={clientMoveIn(client)}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Property Selection */}
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <BusinessIcon fontSize="small" color="primary" />
                  Select Property <span style={{ color: "#ef4444" }}>*</span>
                </Typography>

                <Autocomplete
                  options={properties}
                  getOptionLabel={(option) =>
                    `${option.name}${
                      option.neighborhood ? ` (${option.neighborhood})` : ""
                    }`
                  }
                  value={selectedProperty}
                  onChange={(_, newValue) => handlePropertyChange(newValue)}
                  loading={loadingProperties}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ width: "100%" }}>
                        <Typography variant="body2">{option.name}</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {option.neighborhood && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {option.neighborhood}
                            </Typography>
                          )}
                          {option.contacts && option.contacts.length > 0 && (
                            <Chip
                              label={`${option.contacts.length} contact${
                                option.contacts.length !== 1 ? "s" : ""
                              }`}
                              size="small"
                              sx={{ height: 18, fontSize: 10 }}
                            />
                          )}
                        </Stack>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Search properties..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <HomeIcon fontSize="small" color="action" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{ mt: 1 }}
                />
              </Paper>
            </Grid>

            {/* Property Contacts */}
            {selectedProperty && (
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      justifyContent: "space-between",
                      alignItems: { xs: "stretch", sm: "center" },
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        flexWrap: "wrap",
                      }}
                    >
                      <ContactMailIcon fontSize="small" color="primary" />
                      Property Contacts
                      {selectedContacts.length > 0 && (
                        <Chip
                          label={`${selectedContacts.length} selected`}
                          size="small"
                          color="primary"
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      )}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      sx={{ flexShrink: 0 }}
                    >
                      {hasContacts && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleSelectAllContacts}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            py: 0.25,
                            px: 1.25,
                            minHeight: 28,
                            fontSize: "0.78rem",
                            borderRadius: 1.5,
                          }}
                        >
                          {selectedContacts.length ===
                          selectedProperty.contacts?.length
                            ? "Deselect All"
                            : "Select All"}
                        </Button>
                      )}
                      {!isDemo && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                          onClick={() => setShowAddContact(!showAddContact)}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            py: 0.25,
                            px: 1.25,
                            minHeight: 28,
                            fontSize: "0.78rem",
                            borderRadius: 1.5,
                          }}
                        >
                          Add Contact
                        </Button>
                      )}
                    </Stack>
                  </Box>

                  {/* Add Contact Form */}
                  <Collapse in={showAddContact}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.02),
                        borderColor: alpha(theme.palette.success.main, 0.3),
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        gutterBottom
                        color="success.main"
                      >
                        Add New Contact
                      </Typography>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Name"
                            placeholder="John Smith"
                            value={newContactName}
                            onChange={(e) => setNewContactName(e.target.value)}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Email"
                            placeholder="john@property.com"
                            type="email"
                            value={newContactEmail}
                            onChange={(e) => setNewContactEmail(e.target.value)}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Title (optional)"
                            placeholder="Leasing Manager"
                            value={newContactTitle}
                            onChange={(e) => setNewContactTitle(e.target.value)}
                          />
                        </Grid>
                      </Grid>
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                        mt={1.5}
                      >
                        <Button
                          size="small"
                          startIcon={<CancelIcon />}
                          onClick={() => {
                            setShowAddContact(false);
                            resetNewContactForm();
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={
                            savingContact ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <SaveIcon />
                            )
                          }
                          onClick={handleAddContact}
                          disabled={
                            savingContact ||
                            !newContactName.trim() ||
                            !newContactEmail.trim()
                          }
                        >
                          {savingContact ? "Saving..." : "Save Contact"}
                        </Button>
                      </Stack>
                    </Paper>
                  </Collapse>

                  {/* Contacts List */}
                  {hasContacts ? (
                    <List dense disablePadding>
                      {selectedProperty.contacts!.map((contact) => {
                        const isSelected = selectedContacts.some(
                          (c) => c.id === contact.id
                        );
                        return (
                          <ListItem
                            key={contact.id}
                            disablePadding
                            sx={{
                              borderRadius: 1,
                              mb: 0.5,
                              border: `1px solid ${
                                isSelected
                                  ? theme.palette.primary.main
                                  : "transparent"
                              }`,
                              bgcolor: isSelected
                                ? alpha(theme.palette.primary.main, 0.04)
                                : "transparent",
                            }}
                          >
                            <ListItemButton
                              onClick={() => handleToggleContact(contact)}
                              dense
                              sx={{ borderRadius: 1 }}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Checkbox
                                  edge="start"
                                  checked={isSelected}
                                  disableRipple
                                  size="small"
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant="body2" fontWeight={500}>
                                    {contact.name}
                                    {contact.title && (
                                      <Typography
                                        component="span"
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ ml: 1 }}
                                      >
                                        • {contact.title}
                                      </Typography>
                                    )}
                                  </Typography>
                                }
                                secondary={
                                  isDemo
                                    ? "info@locatenowatl.com"
                                    : contact.email
                                }
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  ) : (
                    <Box
                      sx={{
                        py: 3,
                        textAlign: "center",
                        color: "text.secondary",
                      }}
                    >
                      <ContactMailIcon
                        sx={{ fontSize: 40, mb: 1, opacity: 0.5 }}
                      />
                      <Typography variant="body2">
                        No contacts for this property yet.
                      </Typography>
                      <Typography variant="caption">
                        Click "Add Contact" to add one.
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            )}

            {/* Additional Details */}
            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <MessageIcon fontSize="small" color="primary" />
                  Additional Details
                </Typography>

                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  {/* Beds/Baths */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Beds/Baths"
                      placeholder="2 Bed / 2 Bath"
                      value={bedsBaths}
                      onChange={(e) => setBedsBaths(e.target.value)}
                    />
                  </Grid>

                  {/* Additional Comments */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Additional Comments"
                      placeholder="Any special requirements or notes for the property..."
                      value={additionalComments}
                      onChange={(e) => setAdditionalComments(e.target.value)}
                      multiline
                      rows={2}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Your Contact Info */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Your Contact Info
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Name"
                    value={locatorName}
                    onChange={(e) => setLocatorName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Title"
                    value={locatorJobTitle}
                    onChange={(e) => setLocatorJobTitle(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Phone"
                    value={locatorPhone}
                    onChange={(e) => setLocatorPhone(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Email"
                    value={locatorEmail}
                    onChange={(e) => setLocatorEmail(e.target.value)}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          /* Preview Mode */
          <Box>
            <Alert
              severity="info"
              sx={{ mb: 2, py: { xs: 0.5, sm: 1 } }}
              icon={isMobile ? false : undefined}
            >
              {isMobile ? (
                <Typography variant="caption" fontWeight={600}>
                  {selectedContacts.length === 0
                    ? "Preview \u2192 download copy"
                    : `Preview \u2192 ${selectedContacts.length} contact${
                        selectedContacts.length !== 1 ? "s" : ""
                      }`}
                </Typography>
              ) : selectedContacts.length === 0 ? (
                <>
                  Previewing the <strong>downloadable card</strong> for{" "}
                  <strong>{selectedProperty?.name}</strong> — no contacts are
                  selected, so nothing will be emailed.
                </>
              ) : (
                <>
                  Previewing email to{" "}
                  <strong>{selectedContacts.length} contact(s)</strong>:{" "}
                  {selectedContacts.map((c) => c.name).join(", ")}
                </>
              )}
            </Alert>
            <Paper
              variant="outlined"
              sx={{
                p: 0,
                borderRadius: 2,
                overflow: "hidden",
                maxWidth: 640,
                mx: "auto",
                width: "100%",
              }}
            >
              <Box
                ref={previewScaleContainerRef}
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  width: "100%",
                  height: isMobile
                    ? `${Math.max(240, 600 * previewScale)}px`
                    : "60vh",
                }}
              >
                <iframe
                  srcDoc={previewHtml}
                  style={{
                    width: isMobile ? "600px" : "100%",
                    height: isMobile ? "600px" : "100%",
                    border: "none",
                    display: "block",
                    transform: isMobile
                      ? `scale(${previewScale})`
                      : undefined,
                    transformOrigin: "top left",
                  }}
                  title="Guest Card Preview"
                />
              </Box>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.25, sm: 2 },
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          size={isMobile ? "small" : "medium"}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>

        <Button
          variant="outlined"
          onClick={() => setShowPreview(!showPreview)}
          startIcon={<PreviewIcon sx={{ fontSize: 16 }} />}
          disabled={!selectedProperty}
          size="small"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 1.5,
            px: 1.5,
            minHeight: 34,
            "& .MuiButton-startIcon": { mr: 0.75 },
          }}
        >
          {showPreview ? "Edit" : "Preview"}
        </Button>

        {!isDemo && (
          <Button
            variant="outlined"
            onClick={handleDownload}
            disabled={loading || outcome !== null || !selectedProperty}
            startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            size="small"
            aria-label="Download guest card and record the registration"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 1.5,
              px: 1.5,
              minHeight: 34,
              "& .MuiButton-startIcon": { mr: 0.75 },
            }}
          >
            Download card
          </Button>
        )}

        {!isDemo && (
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={
              loading ||
              selectedContacts.length === 0 ||
              outcome !== null ||
              !gmailStatus.connected
            }
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SendIcon sx={{ fontSize: 18 }} />
              )
            }
            size={isMobile ? "small" : "medium"}
            sx={{
              ml: "auto",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 1.5,
              whiteSpace: "nowrap",
              minHeight: 34,
            }}
          >
            {loading ? "Sending..." : "Send"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GuestCardDialog;
